/**
 * Game URL resolution — maps game slugs to playable game URLs.
 *
 * In local dev, games are served by the game-server app at port 5173.
 * In production, games live at games.genieworlds.games/<slug>/.
 *
 * The 5 Sprint 1 templates have real game-server entries. All other
 * placeholder games use "#" as their playUrl — the Play button is
 * disabled for those (shows "Coming Soon").
 */

/** Game-server port for local development. */
const GAME_SERVER_PORT = 5173;

/** Production base URL for published games. */
const PRODUCTION_BASE = "https://games.genieworlds.games";

/** Slugs that have real game-server entries (Sprint 1 templates). */
const PLAYABLE_SLUGS = new Set([
	"endless-runner",
	"idle-clicker",
	"block-puzzle",
	"word-trivia",
	"board-game",
]);

/**
 * Check if we're in local development (portal running on localhost).
 */
function isLocalDev(): boolean {
	try {
		return (
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1"
		);
	} catch {
		return false;
	}
}

/**
 * Get the playable URL for a game slug. Returns the game-server URL
 * in local dev, or the production URL in production.
 *
 * Returns null if the game doesn't have a playable template yet.
 */
export function getGamePlayUrl(slug: string): string | null {
	// Check placeholder games with custom playUrl first
	// For now, only Sprint 1 templates are playable
	if (!isPlayable(slug)) return null;

	if (isLocalDev()) {
		return `http://localhost:${GAME_SERVER_PORT}/games/${slug}/`;
	}
	return `${PRODUCTION_BASE}/${slug}/`;
}

/**
 * Check if a game slug has a playable template.
 */
export function isPlayable(slug: string): boolean {
	return PLAYABLE_SLUGS.has(slug);
}

/**
 * Map archetype to its default template slug. Used when the portal
 * game entry's slug doesn't match the archetype slug (e.g., "neon-rush"
 * is an endless-runner game).
 */
const ARCHETYPE_TO_SLUG: Record<string, string> = {
	"endless-runner": "endless-runner",
	"idle-clicker": "idle-clicker",
	"block-puzzle": "block-puzzle",
	"word-trivia": "word-trivia",
	"board-game": "board-game",
};

/**
 * Resolve a game's play URL. Tries the game's own slug first, then
 * falls back to the archetype's default template slug.
 *
 * This lets "neon-rush" (an endless-runner game) resolve to the
 * endless-runner template.
 */
export function resolveGamePlayUrl(
	slug: string,
	archetype: string,
): string | null {
	// Direct match (game slug matches template slug)
	const direct = getGamePlayUrl(slug);
	if (direct) return direct;

	// Archetype fallback (game slug is different, but the archetype has a template)
	const templateSlug = ARCHETYPE_TO_SLUG[archetype];
	if (templateSlug) return getGamePlayUrl(templateSlug);

	return null;
}
